import React, { useState } from 'react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// To render this component, you would typically use ReactDOM in a project setup:
// import ReactDOM from 'react-dom/client';
// const root = ReactDOM.createRoot(document.getElementById('root'));
// root.render(<App />);


//================================================================
// STYLES
// We'll define all styles here for better organization.
//================================================================
const styles = {
    // General styles
    appContainer: {
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        color: 'white',
        fontFamily: "'Inter', sans-serif",
        overflowX: 'hidden', // Prevent horizontal scroll
    },
    section: {
        minHeight: '100vh',
        padding: '60px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxSizing: 'border-box',
    },
    badge: {
        background: 'rgba(139, 92, 246, 0.2)',
        border: '1px solid rgba(139, 92, 246, 0.5)',
        borderRadius: '25px',
        padding: '8px 20px',
        marginBottom: '30px',
        fontSize: '14px',
        color: '#a855f7',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    // Navbar (Assuming it's in a separate file)
    navbar: {
        width: '100%',
        padding: '20px 40px',
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000,
        boxSizing: 'border-box',
    },
    navLogo: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    navLinks: {
        display: 'flex',
        gap: '30px',
    },
    navLink: {
        color: '#e5e7eb',
        textDecoration: 'none',
        fontWeight: 500,
        transition: 'color 0.3s ease',
    },
    navButton: {
      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    // Hero Section (GlobalAISociety)
    heroContainer: {
        textAlign: 'center',
    },
    heroTitle: {
      fontSize: 'clamp(2.5rem, 8vw, 4rem)', // Responsive font size
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '20px',
      background: 'linear-gradient(135deg, #60a5fa 0%, #8b5cf6 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      lineHeight: '1.2'
    },
    heroSubtitle: {
      fontSize: 'clamp(1rem, 4vw, 1.2rem)',
      textAlign: 'center',
      marginBottom: '40px',
      color: '#9ca3af',
      maxWidth: '600px',
      lineHeight: '1.6'
    },
    primaryButton: {
      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      marginBottom: '60px',
    },
    statsContainer: {
      display: 'flex',
      gap: '60px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    statItem: { textAlign: 'center' },
    statNumber: { fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '8px' },
    statLabel: { fontSize: '14px', color: '#9ca3af' },
    purpleText: { color: '#a855f7' },
    greenText: { color: '#10b981' },
    downArrow: {
      position: 'absolute',
      bottom: '40px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '24px',
      color: '#9ca3af',
    },
    // Benefits Section
    benefitsHeader: { textAlign: 'center', marginBottom: '80px', width: '100%', maxWidth: '900px' },
    benefitsTitle: {
      fontSize: 'clamp(2rem, 7vw, 3.5rem)',
      fontWeight: 'bold',
      marginBottom: '20px',
      lineHeight: '1.2'
    },
    titleHighlight: {
      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    benefitsSubtitle: {
      fontSize: 'clamp(1rem, 4vw, 1.2rem)',
      color: '#9ca3af',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    benefitsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '40px',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
    },
    benefitCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      padding: '40px 30px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      cursor: 'default',
    },
    iconContainer: {
      width: '60px',
      height: '60px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '24px',
      fontSize: '24px',
      color: 'white',
    },
    purpleIcon: { background: 'linear-gradient(135deg, #8b5cf6, #a855f7)' },
    blueIcon: { background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' },
    greenIcon: { background: 'linear-gradient(135deg, #10b981, #059669)' },
    cyanIcon: { background: 'linear-gradient(135deg, #06b6d4, #0891b2)' },
    benefitTitle: { fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '16px', color: 'white' },
    benefitDescription: { fontSize: '1rem', color: '#9ca3af', lineHeight: '1.6' },
    // Join/Form Section
    formSectionContainer: {
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #0f3460 0%, #16213e 50%, #1a1a2e 100%)',
    },
    formHero: {
      textAlign: 'center',
      marginBottom: '60px',
      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(16, 185, 129, 0.15))',
      borderRadius: '20px',
      padding: '60px 40px',
      width: '100%',
      maxWidth: '900px',
      position: 'relative',
      overflow: 'hidden'
    },
    formHeroTitle: {
      fontSize: 'clamp(2.5rem, 7vw, 3.5rem)',
      fontWeight: 'bold',
      marginBottom: '20px',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      lineHeight: '1.2'
    },
    formHeroSubtitle: {
      fontSize: 'clamp(1rem, 4vw, 1.2rem)',
      color: '#e5e7eb',
      maxWidth: '600px',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    formContainer: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      padding: '40px',
      width: '100%',
      maxWidth: '800px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      boxSizing: 'border-box',
    },
    formHeader: { textAlign: 'center', marginBottom: '40px' },
    formTitle: {
      fontSize: 'clamp(1.5rem, 5vw, 2rem)',
      fontWeight: 'bold',
      marginBottom: '12px',
      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '24px',
      marginBottom: '24px',
    },
    inputGroup: { display: 'flex', flexDirection: 'column' },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    required: { color: '#ef4444' },
    input: {
      background: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      padding: '12px 16px',
      fontSize: '16px',
      color: 'white',
      outline: 'none',
      transition: 'all 0.3s ease'
    },
    textarea: {
      background: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      padding: '12px 16px',
      fontSize: '16px',
      color: 'white',
      outline: 'none',
      transition: 'all 0.3s ease',
      resize: 'vertical',
      minHeight: '120px',
      fontFamily: 'inherit'
    },
    submitButton: {
      background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '16px 32px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '24px'
    },
    // Footer
    footer: {
        background: '#1a1a2e',
        padding: '40px 20px',
        textAlign: 'center',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
    footerText: {
        color: '#9ca3af',
        fontSize: '14px',
    },
    // Decorative Elements
    circle1: {
      position: 'absolute',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: 'rgba(139, 92, 246, 0.1)',
      top: '10%',
      left: '10%',
      animation: 'float 6s ease-in-out infinite'
    },
    circle2: {
      position: 'absolute',
      width: '150px',
      height: '150px',
      borderRadius: '50%',
      background: 'rgba(16, 185, 129, 0.1)',
      bottom: '20%',
      right: '15%',
      animation: 'float 8s ease-in-out infinite reverse'
    },
};

const GlobalAISociety1 = () => (
    <section style={{...styles.section, ...styles.heroContainer}}>
        <div style={styles.circle1}></div>
        <div style={styles.circle2}></div>
        
        <div style={styles.badge}>
            <span>⚡</span>
            Join 50,000+ Students Worldwide
        </div>
        
        <h1 style={styles.heroTitle}>
            Global AI Society<br />of Students
        </h1>
        
        <p style={styles.heroSubtitle}>
            Unlock your potential in Artificial Intelligence. Connect, learn, and innovate with the world's largest student AI community.
        </p>
        
        <a href="#join" style={{textDecoration: 'none'}}>
            <button style={styles.primaryButton} className="hover-effect">
                🚀 Start Your AI Journey
            </button>
        </a>
        
        <div style={styles.statsContainer}>
            <div style={styles.statItem}>
                <div style={{...styles.statNumber, ...styles.purpleText}}>50K+</div>
                <div style={styles.statLabel}>Active Members</div>
            </div>
            <div style={styles.statItem}>
                <div style={{...styles.statNumber, ...styles.greenText}}>200+</div>
                <div style={styles.statLabel}>Universities</div>
            </div>
        </div>
        
        <div style={{...styles.downArrow, animation: 'bounce 2s infinite'}}>
            ⌄
        </div>
    </section>
);

const AICommunityBenefits = () => {
    const benefitsData = [
        { icon: '🧠', style: styles.purpleIcon, title: 'AI Expertise Development', description: 'Access cutting-edge AI courses, workshops, and hands-on projects to build expertise.' },
        { icon: '🌐', style: styles.blueIcon, title: 'Global Networking', description: 'Connect with AI enthusiasts, researchers, and industry professionals from around the world.' },
        { icon: '🏆', style: styles.greenIcon, title: 'Industry Certifications', description: 'Earn recognized certifications in AI that boost your resume and increase job prospects.' },
        { icon: '👥', style: styles.purpleIcon, title: 'Collaborative Projects', description: 'Work on real-world AI projects with fellow students, contributing to open-source initiatives.' },
        { icon: '💡', style: styles.blueIcon, title: 'Innovation Challenges', description: 'Participate in AI hackathons and competitions with prizes and recognition opportunities.' },
        { icon: '📚', style: styles.cyanIcon, title: 'Exclusive Resources', description: 'Access premium learning materials, research papers, datasets, and AI tools for members.' }
    ];

    return (
        <section id="benefits" style={styles.section}>
            <div style={styles.benefitsHeader}>
                <div style={styles.badge}>
                    <span>⭐</span>
                    Member Benefits
                </div>
                <h2 style={styles.benefitsTitle}>
                    Why Join Our <span style={styles.titleHighlight}>AI Community</span>?
                </h2>
                <p style={styles.benefitsSubtitle}>
                    Discover the exclusive advantages that await you as a member of the world's premier student AI society.
                </p>
            </div>
            <div style={styles.benefitsGrid}>
                {benefitsData.map((benefit, index) => (
                    <div key={index} style={styles.benefitCard} className="hover-effect-card">
                        <div style={{...styles.iconContainer, ...benefit.style}}>
                            {benefit.icon}
                        </div>
                        <h3 style={styles.benefitTitle}>{benefit.title}</h3>
                        <p style={styles.benefitDescription}>{benefit.description}</p>
                    </div>
                ))}
            </div>
        </section>
    );
};

const JoinAIFuture = () => {
    const [formData, setFormData] = useState({
        fullName: '', email: '', university: '', academicYear: '', message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.fullName || !formData.email || !formData.university || !formData.academicYear) {
            alert('Please fill in all required fields.');
            return;
        }
        setIsSubmitting(true);

        // --- IMPORTANT: Replace with your actual EmailJS credentials ---
        const serviceID = 'service_n36e8hg'; // Your EmailJS Service ID
        const templateID = 'template_i9jkfri'; // Your EmailJS Template ID
        const publicKey = 'VAEdCMXuUKeCOw6gb'; // Your EmailJS Public Key

        // Construct the email body from form data
        const emailBody = `
          New Student Membership Inquiry
          ---------------------------------

          Applicant Details:
          - Full Name: ${formData.fullName}
          - Email: ${formData.email}
          - University: ${formData.university}
          - Academic Year: ${formData.academicYear}

          Message:
          ${formData.message || 'No message provided.'}
        `;
        
        // Prepare the parameters for your EmailJS template
        const templateParams = {
            email_subject: "Student Membership Inquiry", // Subject as requested
            email_body: emailBody.trim()
        };

        emailjs.send(serviceID, templateID, templateParams, publicKey)
            .then((response) => {
                console.log('SUCCESS!', response.status, response.text);
                // --- Alert on Success ---
                alert('Thank you! Your message has been sent successfully.');
                setIsSubmitting(false);
                // Reset form fields
                setFormData({ fullName: '', email: '', university: '', academicYear: '', message: '' });
            })
            .catch((err) => {
                console.error('FAILED...', err);
                // --- Alert on Failure ---
                alert('An error occurred while sending your message. Please try again later.');
                setIsSubmitting(false);
            });
    };

    return (
        <section id="join" style={{...styles.section, ...styles.formSectionContainer}}>
            <div style={styles.formHero}>
                <h2 style={styles.formHeroTitle}>Join the Future of AI</h2>
                <p style={styles.formHeroSubtitle}>Be part of the next generation of AI innovators and leaders.</p>
            </div>

            <div id="contact" style={styles.formContainer}>
                <div style={styles.formHeader}>
                    <h3 style={styles.formTitle}>Have Questions? Let's Connect!</h3>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGrid} className="form-grid-responsive">
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>📝 Full Name <span style={styles.required}>*</span></label>
                            <input type="text" name="fullName" placeholder="Enter your full name" value={formData.fullName} onChange={handleInputChange} style={styles.input} required className="input-focus" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>📧 Email Address <span style={styles.required}>*</span></label>
                            <input type="email" name="email" placeholder="your-email@university.edu" value={formData.email} onChange={handleInputChange} style={styles.input} required className="input-focus" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>🎓 University <span style={styles.required}>*</span></label>
                            <input type="text" name="university" placeholder="Your university name" value={formData.university} onChange={handleInputChange} style={styles.input} required className="input-focus" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>📅 Academic Year <span style={styles.required}>*</span></label>
                            <select name="academicYear" value={formData.academicYear} onChange={handleInputChange} required style={{...styles.input, appearance: 'none'}} className="input-focus">
                                <option value="">Select your year</option>
                                <option value="freshman">Freshman</option>
                                <option value="sophomore">Sophomore</option>
                                <option value="junior">Junior</option>
                                <option value="senior">Senior</option>
                                <option value="graduate">Graduate</option>
                            </select>
                        </div>
                        <div style={{...styles.inputGroup, gridColumn: '1 / -1'}}>
                            <label style={styles.label}>💬 Your Message</label>
                            <textarea name="message" placeholder="Your questions or interests..." value={formData.message} onChange={handleInputChange} style={styles.textarea} className="input-focus" />
                        </div>
                    </div>
                    <button type="submit" style={styles.submitButton} className="hover-effect" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : '🚀 Send Your Query'}
                    </button>
                </form>
            </div>
        </section>
    );
};


//================================================================
// MAIN APP COMPONENT
// This brings all the other components together.
//================================================================
const GlobalAISociety = () => {
  return (
    <div style={styles.appContainer}>
      {/* Global Styles and Animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap');
        
        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
          40% { transform: translateX(-50%) translateY(-10px); }
          60% { transform: translateX(-50%) translateY(-5px); }
        }

        .hover-effect:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        }

        .hover-effect-card:hover {
          transform: translateY(-8px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(139, 92, 246, 0.3);
        }

        .hover-effect-nav:hover {
            color: #d8b4fe;
        }

        .input-focus:focus {
          border-color: rgba(139, 92, 246, 0.5);
          background: rgba(255, 255, 255, 0.12);
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
        }
        
        select option {
          background: #1a1a2e;
          color: white;
        }

        /* Mobile Responsiveness */
        @media (min-width: 768px) {
            .form-grid-responsive {
                grid-template-columns: 1fr 1fr;
            }
        }
        
        @media (max-width: 768px) {
            .navbar {
                flex-direction: column;
                gap: 15px;
                padding: 15px;
            }
            .nav-links {
                display: none; /* Hiding links on mobile for simplicity */
            }
            .nav-cta {
                display: none; /* Hiding cta on mobile for simplicity */
            }
            .section {
                padding: 80px 20px;
            }
        }
      `}</style>

      <Navbar />
      <main>
        <GlobalAISociety1 />
        <AICommunityBenefits />
        <JoinAIFuture />
      </main>
      <Footer />
    </div>
  );
}

export default GlobalAISociety;